import { jsPDF } from 'jspdf'
import { tenantConfig } from '../config/tenant'

/**
 * Client-side "save my results as a PDF". We deliberately generate the PDF in the
 * browser and let the visitor download it, rather than emailing it: LoanLoop's
 * Resend account is for transactional broker/tenant mail, not bulk customer sends
 * (deliverability + compliance). The lead is still captured separately so a broker
 * follows up. No em-dashes in any visible copy.
 */

export interface ResultsPdfData {
  contact: { name: string; email: string; phone: string }
  repayment: { loanAmount: number; ratePercent: number; termYears: number; type: 'PI' | 'IO'; monthly: number }
  extra?: { amount: number; frequency: string; interestSaved: number; timeSavedLabel: string }
  offset?: { balance: number; interestSaved: number; timeSavedLabel: string }
  property?: { price: number; deposit: number; state: string; lvr: number; stampDuty: number; lmi: number }
}

const money = (v: number) =>
  v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })

/** Convert a #RRGGBB hex string to an [r,g,b] triple for jsPDF. */
function hexRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)]
}

export function buildResultsPdf(d: ResultsPdfData): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const M = 48 // page margin
  const ink = hexRgb(tenantConfig.primaryColor)
  const accent = hexRgb(tenantConfig.accentColor)

  // Header band
  doc.setFillColor(ink[0], ink[1], ink[2])
  doc.rect(0, 0, pageW, 96, 'F')
  doc.setFillColor(accent[0], accent[1], accent[2])
  doc.rect(0, 96, pageW, 4, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.text(tenantConfig.name, M, 46)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(12)
  doc.text('Your home loan snapshot', M, 68)
  doc.setFontSize(9)
  doc.setTextColor(210, 210, 210)
  doc.text(
    `Prepared for ${d.contact.name} on ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`,
    M,
    84,
  )

  let y = 140
  doc.setTextColor(20, 20, 20)

  const sectionTitle = (title: string) => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.setTextColor(ink[0], ink[1], ink[2])
    doc.text(title, M, y)
    doc.setDrawColor(225, 225, 220)
    doc.line(M, y + 6, pageW - M, y + 6)
    y += 26
  }
  const row = (label: string, value: string, emphasise = false) => {
    doc.setFont('helvetica', emphasise ? 'bold' : 'normal')
    doc.setFontSize(emphasise ? 12 : 11)
    doc.setTextColor(emphasise ? 20 : 90, emphasise ? 20 : 90, emphasise ? 20 : 90)
    doc.text(label, M, y)
    doc.setTextColor(20, 20, 20)
    doc.text(value, pageW - M, y, { align: 'right' })
    y += emphasise ? 22 : 18
  }

  // Repayment
  sectionTitle('Repayment')
  row('Loan amount', money(d.repayment.loanAmount))
  row('Interest rate', `${d.repayment.ratePercent.toFixed(2)}%`)
  row('Term', `${d.repayment.termYears} years`)
  row('Repayment type', d.repayment.type === 'IO' ? 'Interest only' : 'Principal and interest')
  row('Estimated repayment', `${money(d.repayment.monthly)} / month`, true)
  y += 8

  // Extra repayments
  if (d.extra && d.extra.amount > 0) {
    sectionTitle('Extra repayments, pay off faster')
    row('Extra repayment', `${money(d.extra.amount)} ${d.extra.frequency}`)
    row('Interest saved', money(d.extra.interestSaved), true)
    row('Time saved', d.extra.timeSavedLabel)
    y += 8
  }

  // Offset
  if (d.offset && d.offset.balance > 0) {
    sectionTitle('Offset savings')
    row('Offset balance', money(d.offset.balance))
    row('Interest saved', money(d.offset.interestSaved), true)
    row('Time saved', d.offset.timeSavedLabel)
    y += 8
  }

  // Property costs
  if (d.property) {
    sectionTitle('Property costs')
    row('Property price', money(d.property.price))
    row('Deposit', money(d.property.deposit))
    row('LVR', `${d.property.lvr.toFixed(1)}%`)
    row('Stamp duty (est.)', money(d.property.stampDuty))
    row('LMI (est.)', money(d.property.lmi))
    y += 8
  }

  // Disclaimer + contact footer
  doc.setDrawColor(225, 225, 220)
  doc.line(M, y, pageW - M, y)
  y += 18
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(9)
  doc.setTextColor(120, 120, 120)
  const disclaimer =
    'These figures are indicative only and do not constitute credit assistance or a loan offer. ' +
    'Your actual rate, repayments and eligibility depend on your full financial situation and lender ' +
    'assessment. One of our brokers will confirm the real numbers with you.'
  doc.text(doc.splitTextToSize(disclaimer, pageW - M * 2), M, y)
  y += 44

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(ink[0], ink[1], ink[2])
  doc.text(`Talk to a broker at ${tenantConfig.name}`, M, y)
  y += 16
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(80, 80, 80)
  doc.text(tenantConfig.siteUrl.replace(/^https?:\/\//, ''), M, y)

  return doc
}

/** Build the PDF and trigger a browser download. Returns the filename used. */
export function downloadResultsPdf(d: ResultsPdfData): string {
  const doc = buildResultsPdf(d)
  const slug = tenantConfig.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  const filename = `${slug}-home-loan-snapshot.pdf`
  doc.save(filename)
  return filename
}
